import { Section, SectionTitle, StatusItem, StatusValue } from './styles';

import { useAppSelector } from 'features/app/hooks';
import {
  normalizeGreetingPreference,
  resolveAstSignMailEligibility,
  type AstSignMailFlag,
} from 'features/ast-signmail/helpers';
import {
  selectAstSignMailMode,
  selectHasAstOwnerUserId,
} from 'features/ast-signmail/store';
import { callLanguageCodeSelector } from 'features/call/call-base/store/selectors';
import { selectDeafHasConnected } from 'features/call/call-deaf/store';
import { selectIsH2DPrimaryHearingPartyDisconnected } from 'features/call/call-hearing-to-deaf/store';
import {
  isEmergencyCallSelector,
  isEmergencyCallbackSelector,
} from 'features/call/vrs-call/store/vrsCallSelectors';
import { FeatureFlagService } from 'features/feature-toggle/services';
import { getLocalSignMailStartBlockReason } from 'features/signmail/helpers';
import {
  signMailCoreResolvedGreetingPreferenceSelector,
  signMailIsMailboxFullSelector,
} from 'features/signmail/store';

export const AstTab = () => {
  const coreResolvedGreetingPreference = useAppSelector(
    signMailCoreResolvedGreetingPreferenceSelector
  );
  const callLanguageCode = useAppSelector(callLanguageCodeSelector);
  const astSignMailMode = useAppSelector(selectAstSignMailMode);
  const isMailboxFull = useAppSelector(signMailIsMailboxFullSelector);
  const hasDeafConnected = useAppSelector(selectDeafHasConnected);
  const isEmergencyCall = useAppSelector(isEmergencyCallSelector);
  const isEmergencyCallback = useAppSelector(isEmergencyCallbackSelector);
  const isHearingPartyDisconnected = useAppSelector(
    selectIsH2DPrimaryHearingPartyDisconnected
  );
  const hasOwnerUserId = useAppSelector(selectHasAstOwnerUserId);
  const localSignMailStartBlockReason = useAppSelector(
    getLocalSignMailStartBlockReason
  );

  const normalizedGreetingPreference = normalizeGreetingPreference(
    coreResolvedGreetingPreference
  );
  const astEligibility = normalizedGreetingPreference
    ? resolveAstSignMailEligibility({
        greetingPreference: normalizedGreetingPreference,
        callLanguageCode,
        astSignMailMode,
        hasDeafConnected,
        isMailboxFull: !!isMailboxFull,
        isEmergencyCall,
        isEmergencyCallback,
        isHearingPartyDisconnected,
        hasOwnerUserId,
        isLocalSignMailBlocked: localSignMailStartBlockReason !== null,
      })
    : undefined;
  const astSignmailFlag =
    astEligibility?.astSignmailFlag ??
    (FeatureFlagService.getInstance().flags.astSignmail as
      AstSignMailFlag | undefined);

  const isLdEnabled = astSignmailFlag?.enabled ?? false;
  const isGreetingAllowed = astEligibility?.isGreetingAllowed ?? false;
  const isLanguageAllowed = astEligibility?.isLanguageAllowed ?? false;
  const isAstSignMailEnabled = astEligibility?.isAstEligible ?? false;
  const greetingTypeLabel = astEligibility?.greetingPreferenceKey ?? 'Unknown';
  const callLanguageLabel = callLanguageCode || 'Unknown';
  const allowedLanguagesLabel =
    astSignmailFlag?.allowedLanguages?.join(', ') || 'None';

  let astSignMailModeLabel: string;
  if (astSignMailMode === undefined) {
    astSignMailModeLabel = 'Not loaded';
  } else if (astSignMailMode === 1) {
    astSignMailModeLabel = 'Enabled';
  } else {
    astSignMailModeLabel = 'Disabled';
  }

  let mailboxFullLabel: string;
  if (isMailboxFull === undefined) {
    mailboxFullLabel = 'Not loaded';
  } else {
    mailboxFullLabel = isMailboxFull ? 'Yes' : 'No';
  }

  return (
    <Section>
      <SectionTitle variant='subtitle2'>AST Signmail</SectionTitle>
      <StatusItem>
        AST Signmail Enabled:{' '}
        <StatusValue>{isAstSignMailEnabled ? 'Yes' : 'No'}</StatusValue>
      </StatusItem>
      <hr />
      <StatusItem>
        AST LaunchDarkly:{' '}
        <StatusValue>{isLdEnabled ? 'Yes' : 'No'}</StatusValue>
      </StatusItem>
      <StatusItem>
        AST SignMail Mode (Core):{' '}
        <StatusValue>{astSignMailModeLabel}</StatusValue>
      </StatusItem>
      <StatusItem>
        Allowed Languages: <StatusValue>{allowedLanguagesLabel}</StatusValue>
      </StatusItem>
      <StatusItem>
        Greeting Allowed:{' '}
        <StatusValue>
          {isGreetingAllowed ? 'Yes' : 'No'} ({greetingTypeLabel})
        </StatusValue>
      </StatusItem>
      <StatusItem>
        Language Allowed:{' '}
        <StatusValue>
          {isLanguageAllowed ? 'Yes' : 'No'} ({callLanguageLabel})
        </StatusValue>
      </StatusItem>
      <StatusItem>
        Mailbox Full: <StatusValue>{mailboxFullLabel}</StatusValue>
      </StatusItem>
    </Section>
  );
};
